import {
  Modal,
  IconButton,
  Button,
  ButtonVariant,
  Input,
  TruncateWithTooltip,
  IconButtonSize,
  IconButtonVariant,
  Show
} from "shared/components";
import { FC, useEffect } from "react";
import { InformationCircleIcon } from "@hugeicons-pro/core-solid-rounded";
import { Cancel01Icon } from "@hugeicons-pro/core-stroke-rounded";
import { HugeiconsIcon } from "@hugeicons/react";

import { useMutation } from "@tanstack/react-query";
import { instance } from "api/apiBase";
import { toast } from "react-toastify";
import { getErrorMessage } from "utils/error";
import { Money } from "types/landlord/reports";
import { useAuthContext } from "providers/Auth";
import { formatToCurrency } from "shared/utils/currency";
import { useFormTyped } from "shared/hooks";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "shared/components/Tooltip";
import { CriteriaRentType } from "shared/types";
import { cx } from "shared/utils";

interface Props {
  opened: boolean;
  onClose: () => void;
  applicationId: string;
  onSuccess?: () => void;
  applicationRentAmount: ApplicationRentAmount;
  criteriaRentType: CriteriaRentType;
}

interface RentAmountData {
  rent_amount?: Money;
  home_rent_amount?: Money;
  site_rent_amount?: Money;
}

interface ApplicationRentAmount {
  monthly_rent_amount?: Money;
  rent_amount?: Money;
  home_rent_amount?: Money;
  site_rent_amount?: Money;
  housing_assistance_voucher_amount?: Money;
  pet_rent_amount?: Money;
  previous_rent_amount?: Money;
}

interface RentFormData {
  rent_amount: number;
  home_rent_amount: number;
  site_rent_amount: number;
}

const useUpdateRentMutation = (
  applicationId: string,
  onClose: () => void,
  onSuccess?: () => void
) => {
  return useMutation({
    mutationFn: (rentData?: RentAmountData) => {
      return instance.patch(
        `/portal/v1/applications/${applicationId}/rents`,
        rentData
      );
    },
    onSuccess: () => {
      toast.success("Application rent successfully updated");
      onSuccess?.();
      onClose();
    },
    onError: (error) => {
      toast.error(`Error updating application rent: ${getErrorMessage(error)}`);
    }
  });
};

export const EditApplicationRentModal: FC<Props> = ({
  opened,
  onClose,
  applicationId,
  applicationRentAmount,
  onSuccess,
  criteriaRentType
}) => {
  const { pms, type } = useAuthContext();
  const homeSiteRentEnabled = pms.isManageAmerica || type.isHomeSiteRentEnabled;

  // Initialize form with current values
  const initialValues: RentFormData = {
    rent_amount: (applicationRentAmount.rent_amount?.cents || 0) / 100,
    home_rent_amount:
      (applicationRentAmount.home_rent_amount?.cents || 0) / 100,
    site_rent_amount: (applicationRentAmount.site_rent_amount?.cents || 0) / 100
  };

  const rentForm = useFormTyped<RentFormData>(initialValues);

  const { mutate: updateRent, isPending } = useUpdateRentMutation(
    applicationId,
    onClose,
    onSuccess
  );

  // Reset form when modal opens/closes or applicationRentAmount changes
  useEffect(() => {
    if (!opened) return;

    rentForm.handleChange(
      "rent_amount",
      () => (applicationRentAmount.rent_amount?.cents || 0) / 100
    );
    rentForm.handleChange(
      "home_rent_amount",
      () => (applicationRentAmount.home_rent_amount?.cents || 0) / 100
    );
    rentForm.handleChange(
      "site_rent_amount",
      () => (applicationRentAmount.site_rent_amount?.cents || 0) / 100
    );
  }, [opened, applicationRentAmount]);

  const handleFormSubmit = (formData: RentFormData) => {
    const rentData: RentAmountData = homeSiteRentEnabled
      ? {
          home_rent_amount: {
            cents: formData.home_rent_amount * 100,
            currency: "USD"
          },
          site_rent_amount: {
            cents: formData.site_rent_amount * 100,
            currency: "USD"
          }
        }
      : {
          rent_amount: { cents: formData.rent_amount * 100, currency: "USD" }
        };

    updateRent(rentData);
  };

  const calculateTotalRentCents = () => {
    let baseRent = 0;
    // Mirror the header breakdown (ApplicationRentSection): only split into
    // home/site when the partner actually uses home/site rent. Otherwise the
    // unsplit rent_amount is the source of truth — gating the whole branch on
    // homeSiteRentEnabled keeps display and submit consistent.
    if (homeSiteRentEnabled) {
      if (criteriaRentType !== CriteriaRentType.Site)
        baseRent += rentForm.values.home_rent_amount || 0;
      if (criteriaRentType !== CriteriaRentType.Home)
        baseRent += rentForm.values.site_rent_amount || 0;
    } else baseRent += rentForm.values.rent_amount || 0;

    const assistanceRent =
      (applicationRentAmount.housing_assistance_voucher_amount?.cents || 0) /
      100;
    const petRent = (applicationRentAmount.pet_rent_amount?.cents || 0) / 100;
    const result = (baseRent - assistanceRent + petRent) * 100;
    if (result < 0) return 0;
    return result;
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      panelClassName="flex flex-col w-[650px]"
    >
      <div className="flex justify-between p-6 border-b border-alpha-100 relative">
        <h1 className="text-xl text-content-1000 font-bold">
          Edit application rent
        </h1>
        <IconButton
          onClick={onClose}
          HugeIcon={Cancel01Icon}
          variant={IconButtonVariant.TEXT_BASE}
          size={IconButtonSize.LG}
        />
      </div>
      <div className="bg-warning-50 grow-0 px-6 py-4 flex gap-4 items-center border-b border-alpha-100">
        <HugeiconsIcon
          icon={InformationCircleIcon}
          className="text-warning-500 h-12 w-12"
        />
        <p className="text-sm text-warning-700 font-medium">
          Please note that editing the application rent will lead to a
          recalculation of the &quot;income to rent&quot; and &quot;debt to
          income&quot; metrics. Decision recommendations may change
        </p>
      </div>
      <form
        onSubmit={rentForm.onSubmit({
          onSuccess: handleFormSubmit
        })}
        className="h-full grow-1 overflow-y-auto"
      >
        <div className="space-y-4 text-content-1000 px-6 py-4">
          <div className="flex items-center gap-4 text-sm">
            {homeSiteRentEnabled ? (
              <>
                <Show if={criteriaRentType !== "site"}>
                  <div
                    className={cx(
                      criteriaRentType === "home" ? "w-[281px]" : "w-[124px]"
                    )}
                  >
                    <Input
                      id="home-rent"
                      label="Home rent"
                      type="money"
                      placeholder={(
                        (applicationRentAmount.home_rent_amount?.cents || 0) /
                        100
                      ).toString()}
                      {...rentForm.register(
                        "home_rent_amount",
                        (value) => parseFloat(value) || 0
                      )}
                      min={0}
                      max={99999}
                      required
                      hideErrorMessage
                    />
                  </div>
                </Show>
                <Show if={criteriaRentType === "both"}>
                  <span className="text-content-1000 font-medium pt-6">+</span>
                </Show>
                <Show if={criteriaRentType !== "home"}>
                  <div
                    className={cx(
                      criteriaRentType === "site" ? "w-[281px]" : "w-[124px]"
                    )}
                  >
                    <Input
                      id="site-rent"
                      label="Site rent"
                      type="money"
                      placeholder={(
                        (applicationRentAmount.site_rent_amount?.cents || 0) /
                        100
                      ).toString()}
                      {...rentForm.register(
                        "site_rent_amount",
                        (value) => parseFloat(value) || 0
                      )}
                      min={0}
                      max={99999}
                      required
                      hideErrorMessage
                    />
                  </div>
                </Show>
              </>
            ) : (
              <div className="w-[281px]">
                <Input
                  id="application-rent"
                  label="Rent amount"
                  type="money"
                  placeholder={(
                    (applicationRentAmount.monthly_rent_amount?.cents || 0) /
                    100
                  ).toString()}
                  {...rentForm.register(
                    "rent_amount",
                    (value) => parseFloat(value) || 0
                  )}
                  min={0}
                  max={99999}
                  required
                  hideErrorMessage
                />
              </div>
            )}
            <span className="text-content-1000 font-medium pt-6">-</span>
            <Tooltip>
              <TooltipTrigger>
                <div className="space-y-2 min-w-[35px] text-content-300 pt-6 text-left">
                  <p className="text-xs">Assistance</p>
                  <p className="font-medium">
                    {formatToCurrency(
                      applicationRentAmount.housing_assistance_voucher_amount ||
                        null
                    )}
                  </p>
                </div>
              </TooltipTrigger>
              <TooltipContent className="bg-alpha-800 rounded-lg border p-4 text-sm text-white max-w-[333px] z-50">
                Housing assistance can be edited for each applicant on their
                application page
              </TooltipContent>
            </Tooltip>
            <span className="text-content-1000 font-medium pt-6">+</span>
            <Tooltip>
              <TooltipTrigger>
                <div className="space-y-2 min-w-[35px] text-content-300 pt-6 text-left">
                  <p className="text-xs">Pet</p>
                  <p className="font-medium">
                    {formatToCurrency({
                      cents: applicationRentAmount.pet_rent_amount?.cents || 0,
                      currency: "USD"
                    })}
                  </p>
                </div>
              </TooltipTrigger>
              <TooltipContent className="bg-alpha-800 rounded-lg border p-4 text-sm text-white max-w-[333px] z-50">
                Pet rent can be edited for each applicant on their application
                page
              </TooltipContent>
            </Tooltip>
            <span className="text-content-1000 font-medium pt-6">=</span>
            <div className="flex-1 space-y-2 w-min-[100px] items-end pt-6 text-right">
              <p className="text-content-300 text-xs">Application rent</p>
              <p className="text-end font-medium">
                <TruncateWithTooltip
                  content={formatToCurrency({
                    cents: calculateTotalRentCents(),
                    currency: "USD"
                  })}
                  maxWidth="100px"
                  tooltipClassName="z-[9999]"
                />
              </p>
            </div>
          </div>
        </div>
        <div className="flex gap-2 justify-end px-6 py-4 border-t border-alpha-100">
          <Button
            variant={ButtonVariant.BASE}
            onClick={() => onClose()}
            className="w-[125px]"
          >
            Cancel
          </Button>
          <Button
            variant={ButtonVariant.PRIMARY}
            type="submit"
            loading={isPending || rentForm.isSubmitting}
            disabled={isPending || rentForm.isSubmitting}
            className="w-[138px]"
          >
            Save changes
          </Button>
        </div>
      </form>
    </Modal>
  );
};
